import {
  ActionList,
  Banner,
  Box,
  Icon,
  InlineStack,
  Modal,
  Popover,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import {
  HomeIcon,
  ProductIcon,
  CollectionIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  StarIcon,
  PageIcon,
  PlusCircleIcon,
} from '@shopify/polaris-icons';
import { useMemo, useState } from 'react';
import {
  useTemplateList,
  useCreateTemplate,
  type TemplateListEntry,
} from '@/queries/sections';
import { requestTemplateSwitch } from '@/queries/stagedDraft';
import { useSelectionStore } from '@/state/selectionStore';
import { usePreviewStore } from '@/state/previewStore';
import { ApiError } from '@/types/api';

// Shopify's page selector is a 2-level drill-down:
//   Root  → Home page (leaf) · Products › · Collections ›
//   Level → ‹ back · search · Default X · <overrides…>
//
// Mobile scope: only Home / Products / Collections are editable templates
// (the app has no cart/search/pages/blogs theme templates). We mirror the
// exact drill-down UX, just with the categories the mobile app supports.

type Level = 'root' | 'product' | 'collection';

const CATEGORY_META: Record<
  Exclude<Level, 'root'>,
  { label: string; base: string; icon: typeof ProductIcon }
> = {
  product: { label: 'Products', base: 'product', icon: ProductIcon },
  collection: { label: 'Collections', base: 'collection', icon: CollectionIcon },
};

export function PageSelector() {
  const { data, isLoading } = useTemplateList();
  const createTemplate = useCreateTemplate();
  const template = useSelectionStore((s) => s.template);
  const previewHandle = usePreviewStore((s) => s.previewHandle);
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState<Level>('root');
  const [query, setQuery] = useState('');
  const [createFor, setCreateFor] = useState<Exclude<Level, 'root'> | null>(null);
  const [newHandle, setNewHandle] = useState('');

  const list: TemplateListEntry[] = data?.templates ?? [];
  const active = list.find((t: TemplateListEntry) => t.template === template);
  const baseLabel = active?.label ?? 'Home page';
  // Reflect the specific resource being previewed (Shopify shows the
  // resource in the bar even though you edit the generic template).
  const activeLabel =
    previewHandle && (template === 'collection' || template === 'product')
      ? `${baseLabel} · ${previewHandle}`
      : baseLabel;
  const activeIcon =
    active?.kind === 'override' && active.parent === 'product'
      ? ProductIcon
      : active?.parent === 'collection' || active?.template === 'collection'
        ? CollectionIcon
        : active?.template === 'product'
          ? ProductIcon
          : HomeIcon;

  const overridesFor = (parent: string) =>
    list.filter((t) => t.kind === 'override' && t.parent === parent);

  const filterByQuery = (items: { content: string }[]) => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.content.toLowerCase().includes(q));
  };

  const close = () => {
    setOpen(false);
    setLevel('root');
    setQuery('');
  };

  const choose = (slug: string) => {
    // Manual page switch: drop any preview-nav handle so the chosen
    // template renders its own/default resource, not a stale collection.
    usePreviewStore.getState().setPreviewHandle(null);
    requestTemplateSwitch(slug);
    close();
  };

  const rootItems = useMemo(() => {
    const items: {
      content: string;
      icon: typeof HomeIcon;
      suffix?: React.ReactNode;
      active?: boolean;
      onAction: () => void;
    }[] = [];
    // Home page — leaf
    if (list.some((t) => t.template === 'index')) {
      items.push({
        content: 'Home page',
        icon: HomeIcon,
        active: template === 'index',
        onAction: () => choose('index'),
      });
    }
    // Products — category (has a base + maybe overrides)
    if (list.some((t) => t.template === 'product' || t.parent === 'product')) {
      items.push({
        content: 'Products',
        icon: ProductIcon,
        suffix: <Icon source={ChevronRightIcon} tone="subdued" />,
        onAction: () => {
          setLevel('product');
          setQuery('');
        },
      });
    }
    // Collections — category
    if (list.some((t) => t.template === 'collection' || t.parent === 'collection')) {
      items.push({
        content: 'Collections',
        icon: CollectionIcon,
        suffix: <Icon source={ChevronRightIcon} tone="subdued" />,
        onAction: () => {
          setLevel('collection');
          setQuery('');
        },
      });
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, template]);

  const categoryItems = useMemo(() => {
    if (level === 'root') return [];
    const meta = CATEGORY_META[level];
    const overrides = overridesFor(meta.base);
    const items: {
      content: string;
      icon: typeof HomeIcon;
      helpText?: string;
      active?: boolean;
      onAction: () => void;
    }[] = [
      {
        content: `Default ${level}`,
        icon: StarIcon,
        active: template === meta.base,
        onAction: () => choose(meta.base),
      },
      ...overrides.map((o) => ({
        content: o.handle ?? o.template,
        icon: PageIcon,
        active: template === o.template,
        onAction: () => choose(o.template),
      })),
      {
        content: 'Create template',
        icon: PlusCircleIcon,
        onAction: () => {
          setOpen(false);
          setCreateFor(level);
        },
      },
    ];
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, list, template]);

  const activator = (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        font: 'inherit',
        color: 'var(--p-color-text)',
        padding: '4px 8px',
        borderRadius: 8,
      }}
    >
      <span style={{ width: 16, height: 16, display: 'inline-flex' }}>
        <Icon source={activeIcon} />
      </span>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{activeLabel}</span>
      <span style={{ width: 14, height: 14, display: 'inline-flex', opacity: 0.6 }}>
        <Icon source={ChevronRightIcon} />
      </span>
    </button>
  );

  const baseForCreate = createFor ? CATEGORY_META[createFor].base : '';
  const handleSlug = newHandle
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const onCreate = () => {
    if (!createFor || !handleSlug) return;
    createTemplate.mutate(
      { base: baseForCreate, handle: handleSlug },
      {
        onSuccess: () => {
          usePreviewStore.getState().setPreviewHandle(null);
          requestTemplateSwitch(`${baseForCreate}.${handleSlug}`);
          setCreateFor(null);
          setNewHandle('');
        },
      },
    );
  };

  return (
    <>
    <Popover
      active={open}
      activator={activator}
      onClose={close}
      preferredPosition="below"
      preferredAlignment="center"
    >
      <div style={{ width: 340, maxHeight: 520, display: 'flex', flexDirection: 'column' }}>
        <Box padding="200">
          <TextField
            label="Search online store"
            labelHidden
            value={query}
            onChange={setQuery}
            autoComplete="off"
            placeholder="Search online store"
            clearButton
            onClearButtonClick={() => setQuery('')}
          />
        </Box>

        {level !== 'root' && (
          <Box
            padding="200"
            paddingBlockStart="100"
            paddingBlockEnd="100"
            borderBlockEndWidth="025"
            borderColor="border"
          >
            <button
              type="button"
              onClick={() => {
                setLevel('root');
                setQuery('');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 4px',
                color: 'var(--p-color-text)',
              }}
            >
              <span style={{ width: 16, height: 16, display: 'inline-flex' }}>
                <Icon source={ArrowLeftIcon} />
              </span>
              <Text as="span" variant="headingSm">
                {CATEGORY_META[level].label}
              </Text>
            </button>
          </Box>
        )}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {isLoading ? (
            <Box padding="400">
              <InlineStack gap="200" align="center">
                <Spinner accessibilityLabel="Loading templates" size="small" />
                <Text as="span">Loading…</Text>
              </InlineStack>
            </Box>
          ) : level === 'root' ? (
            <ActionList actionRole="menuitem" items={filterByQuery(rootItems)} />
          ) : (
            <ActionList actionRole="menuitem" items={filterByQuery(categoryItems)} />
          )}
        </div>
      </div>
    </Popover>

    <Modal
      open={createFor !== null}
      onClose={() => {
        setCreateFor(null);
        setNewHandle('');
      }}
      title={`Create ${createFor ?? ''} template`}
      primaryAction={{
        content: 'Create',
        onAction: onCreate,
        loading: createTemplate.isPending,
        disabled: !handleSlug,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => {
            setCreateFor(null);
            setNewHandle('');
          },
        },
      ]}
    >
      <Modal.Section>
        <InlineStack gap="300" blockAlign="start">
          <div style={{ flex: 1 }}>
            <TextField
              label="Template name"
              value={newHandle}
              onChange={setNewHandle}
              autoComplete="off"
              placeholder="e.g. summer-sale"
              helpText={
                handleSlug
                  ? `Will be saved as ${baseForCreate}.${handleSlug}.json (copied from the default ${createFor ?? ''} template).`
                  : 'Lowercase letters, numbers and dashes only.'
              }
            />
          </div>
        </InlineStack>
        {createTemplate.isError && (
          <Box paddingBlockStart="300">
            <Banner tone="critical">
              {(createTemplate.error as ApiError | undefined)?.message ??
                'Could not create template'}
            </Banner>
          </Box>
        )}
      </Modal.Section>
    </Modal>
    </>
  );
}
